import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LogoIcon, LogoText } from '../components/Logo';
import { useTranslation, Trans } from 'react-i18next';

const S = <strong className="text-white" />;
const A = <a href="mailto:support@scorinova.com" className="text-accent hover:text-accent-light transition-colors" />;

function Terms() {
  const { t, i18n } = useTranslation(['legal', 'common']);
  return (
    <div className="min-h-screen bg-anthracite-950">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('legal:termsTitle')}</title>
        <meta name="description" content={t('legal:termsMetaDesc')} />
        <link rel="canonical" href="https://scorinova.com/terms" />
      </Helmet>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-anthracite-900/95 backdrop-blur-sm border-b border-anthracite-700">
        <div className="flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <LogoIcon className="w-10 h-10" />
            <div>
              <LogoText size="text-xl" />
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">{t('common:aiFootballPredictions')}</p>
            </div>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-anthracite-800 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('common:back')}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="pt-28 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-anthracite-900 rounded-2xl p-8 md:p-12 border border-anthracite-700">

            <h1 className="text-3xl font-bold text-white mb-2">{t('legal:termsHeading')}</h1>
            <p className="text-sm text-gray-500 mb-10">{t('legal:lastUpdated')}</p>

            <div className="space-y-8 text-gray-300 text-[15px] leading-relaxed">

              {/* 1 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t1Head')}</h2>
                <p>{t('legal:t1P1')}</p>
              </section>

              {/* 2 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t2Head')}</h2>
                <p className="mb-3"><Trans i18nKey="legal:t2P1" components={{ s: S }} /></p>
                <p className="mb-3"><Trans i18nKey="legal:t2P2" components={{ s: S }} /></p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li>{t('legal:t2Li1')}</li>
                  <li>{t('legal:t2Li2')}</li>
                  <li>{t('legal:t2Li3')}</li>
                  <li>{t('legal:t2Li4')}</li>
                  <li>{t('legal:t2Li5')}</li>
                </ul>
              </section>

              {/* 3 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t3Head')}</h2>
                <p><Trans i18nKey="legal:t3P1" components={{ s: S }} /></p>
              </section>

              {/* 4 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t4Head')}</h2>
                <p className="mb-3">{t('legal:t4P1')}</p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li><Trans i18nKey="legal:t4Li1" components={{ s: S }} /></li>
                  <li>{t('legal:t4Li2')}</li>
                  <li><Trans i18nKey="legal:t4Li3" components={{ s: S }} /></li>
                  <li>{t('legal:t4Li4')}</li>
                  <li><Trans i18nKey="legal:t4Li5" components={{ s: S }} /></li>
                  <li>{t('legal:t4Li6')}</li>
                  <li>{t('legal:t4Li7')}</li>
                </ul>
              </section>

              {/* 5 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t5Head')}</h2>
                <p className="mb-3"><Trans i18nKey="legal:t5P1" components={{ s: S }} /></p>
                <p>{t('legal:t5P2')}</p>
              </section>

              {/* 6 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t6Head')}</h2>
                <p className="mb-3"><Trans i18nKey="legal:t6P1" components={{ s: S }} /></p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li>{t('legal:t6Li1')}</li>
                  <li>{t('legal:t6Li2')}</li>
                  <li>{t('legal:t6Li3')}</li>
                  <li>{t('legal:t6Li4')}</li>
                  <li>{t('legal:t6Li5')}</li>
                </ul>
                <p className="mt-3">{t('legal:t6P2')}</p>
              </section>

              {/* 7 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t7Head')}</h2>
                <p>{t('legal:t7P1')}</p>
              </section>

              {/* 8 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t8Head')}</h2>
                <p>{t('legal:t8P1')}</p>
              </section>

              {/* 9 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t9Head')}</h2>
                <p className="mb-3">{t('legal:t9P1')}</p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li>{t('legal:t9Li1')}</li>
                  <li>{t('legal:t9Li2')}</li>
                  <li>{t('legal:t9Li3')}</li>
                  <li>{t('legal:t9Li4')}</li>
                  <li>{t('legal:t9Li5')}</li>
                </ul>
              </section>

              {/* 10 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t10Head')}</h2>
                <p className="mb-3">{t('legal:t10P1')}</p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li>{t('legal:t10Li1')}</li>
                  <li>{t('legal:t10Li2')}</li>
                  <li>{t('legal:t10Li3')}</li>
                  <li>{t('legal:t10Li4')}</li>
                  <li>{t('legal:t10Li5')}</li>
                  <li>{t('legal:t10Li6')}</li>
                </ul>
              </section>

              {/* 11 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t11Head')}</h2>
                <p>{t('legal:t11P1')}</p>
              </section>

              {/* 12 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t12Head')}</h2>
                <p>{t('legal:t12P1')}</p>
              </section>

              {/* 13 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t13Head')}</h2>
                <p className="mb-3">{t('legal:t13P1')}</p>
                <ul className="list-disc list-inside space-y-1.5 text-gray-400 ml-2">
                  <li>{t('legal:t13Li1')}</li>
                  <li>{t('legal:t13Li2')}</li>
                  <li>{t('legal:t13Li3')}</li>
                  <li>{t('legal:t13Li4')}</li>
                </ul>
                <p className="mt-3 text-gray-400">{t('legal:t13P2')}</p>
                <ul className="mt-2 space-y-1 text-gray-400 ml-2">
                  <li><Trans i18nKey="legal:t13Help1" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:t13Help2" components={{ s: S }} /></li>
                  <li><Trans i18nKey="legal:t13Help3" components={{ s: S }} /></li>
                </ul>
              </section>

              {/* 14 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t14Head')}</h2>
                <p>{t('legal:t14P1')}</p>
              </section>

              {/* 15 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t15Head')}</h2>
                <p>{t('legal:t15P1')}</p>
              </section>

              {/* 16 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t16Head')}</h2>
                <p>{t('legal:t16P1')}</p>
              </section>

              {/* 17 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t17Head')}</h2>
                <p>{t('legal:t17P1')}</p>
              </section>

              {/* 18 */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-3">{t('legal:t18Head')}</h2>
                <p><Trans i18nKey="legal:t18P1" components={{ a: A }} /></p>
              </section>

            </div>
          </div>

          {/* Footer links */}
          <div className="mt-8 text-center text-xs text-gray-600 space-x-4">
            <Link to="/privacy" className="hover:text-accent transition-colors">{t('common:privacyPolicy')}</Link>
            <span>|</span>
            <Link to="/signup" className="hover:text-accent transition-colors">{t('common:createAccount')}</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Terms;
